import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { AddressModel, IReferenceDataModel, INotifierDetailsModel, NotifierDetailsModel } from '../models/veg-wizard-models';
import { BaseWizardComponent } from '../base-wizard.component';
import { VegWizardDataService } from '../services/veg-wizard-data.service';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap, first, catchError, of, mergeMap, concatMap } from 'rxjs';
import { ReferenceDataService } from '../services/reference-data-service';
import { VegValidators } from '../veg-validators/veg-validators';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { Logger } from '../services/logger-service';
import { SessionService } from 'src/app/services/session.service';
import { FileManagementService } from '../services/file-management.service';
import { ReferencesEnum } from 'src/app/models/shared-models';
import { UtilService } from '../services/util.service';
import { MapComponent } from '../../shared/map/map.component';
import { GlobalValidationMessageComponent } from 'src/app/shared/global-validation-message/global-validation-message.component';

@UntilDestroy()
@Component({
  selector: 'app-notifier-details',
  templateUrl: './notifier-details.component.html',
  styleUrls: ['./notifier-details.component.css'],
  imports: [
    CommonModule,   
    ReactiveFormsModule,
    GlobalValidationMessageComponent,
    NgxFileDropModule
  ],
})

export class NotifierDetailsComponent extends BaseWizardComponent implements OnInit {

  form: FormGroup;
  notifierDetails: INotifierDetailsModel | undefined;

  DEFAULT_COUNTRY_AUSTRALIA_ID: number = ReferencesEnum.australiaCountryId;
  DEFAULT_STATE_QLD_ID: number = ReferencesEnum.queenslandStateId;
  nextButtonClicked = false;
  validEvidenceOfConsentMimeType = '';
  states: IReferenceDataModel[] = [];

  // form getters
  get networkUserIdControl() { return this.form.controls['networkUserId']; }
  get canAttachEvidenceOfConsentControl() { return this.form.controls['canAttachEvidenceOfConsent']; }
  get evidenceOfConsentProvidedControl() { return this.form.controls['evidenceOfConsentProvided']; }
  get phoneOfficerControl() { return this.form.controls['phoneOfficer']; }
  get locationControl() { return this.form.controls['location']; }

  get firstNameControl() { return this.form.controls['firstName']; }
  get lastNameControl() { return this.form.controls['lastName']; }
  get middleNameControl() { return this.form.controls['middleName']; }

  get companyNameControl() { return this.form.controls['companyName']; }
  get phoneControl() { return this.form.controls['phone']; }
  get mobileControl() { return this.form.controls['mobile']; }
  get emailControl() { return this.form.controls['email']; }
  get homeAddressLine1Control() { return this.form.controls['homeAddressLine1']; }
  get homeAddressLine2Control() { return this.form.controls['homeAddressLine2']; }
  get homeAddressSuburbControl() { return this.form.controls['homeAddressSuburb']; }
  get homeAddressPostcodeControl() { return this.form.controls['homeAddressPostcode']; }

  get mailingAddressLine1Control() { return this.form.controls['mailingAddressLine1']; }
  get mailingAddressLine2Control() { return this.form.controls['mailingAddressLine2']; }
  get mailingAddressSuburbControl() { return this.form.controls['mailingAddressSuburb']; }
  get mailingAddressPostcodeControl() { return this.form.controls['mailingAddressPostcode']; }
  get mailingAddressSameAsPhysicalAddressControl() { return this.form.controls['mailingAddressSameAsPhysicalAddress']; }
  get natureOfConsentControl() { return this.form.controls['natureOfConsent']; }

  get isOfficer() { return !!this.vwds.getLandholderConfirmationModel()?.areYouLandsOfficer; }

  canAttachEvidenceOfConsentValid = false;
  evidenceOfConsentValid = false;
  natureOfConsentPresentValid = false;
  evidenceOfConsentFiles: NgxFileDropEntry[] = [];
  evidenceOfConsentFileNames: string[] = [];
  evidenceOfConsentError = '';

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private referenceDataService: ReferenceDataService,
    private sessionService: SessionService,
    private fileManagementService: FileManagementService,
    private utilService: UtilService,
    private logger: Logger,
    router: Router
  ) {
    super();

    this.notifierDetails = this.vwds.getNotifierDetailsModel();

    this.validEvidenceOfConsentMimeType = this.fileManagementService.validEvidenceOfConsentMimeType.toString();

    if (!this.isOfficer) {
      this.form = this.formBuilder.group({
        firstName: [this.notifierDetails?.firstName, [Validators.required, Validators.maxLength(20)]],
        middleName: [this.notifierDetails?.middleName],
        lastName: [this.notifierDetails?.lastName, [Validators.required]],

        companyName: [this.notifierDetails?.companyName, [Validators.maxLength(255)]],

        phone: [this.notifierDetails?.phone],
        mobile: [this.notifierDetails?.mobile],
        email: [this.notifierDetails?.email, [VegValidators.email, Validators.required]],

        homeAddressLine1: [this.notifierDetails?.homeAddress?.line1, [Validators.required]],
        homeAddressLine2: [this.notifierDetails?.homeAddress?.line2],
        homeAddressSuburb: [this.notifierDetails?.homeAddress?.suburb, [Validators.required]],
        homeAddressPostcode: [this.notifierDetails?.homeAddress?.postcode, [Validators.required, VegValidators.postcode]],
        homeAddressStateId: [this.notifierDetails?.homeAddress?.stateId === undefined ? this.DEFAULT_STATE_QLD_ID : this.notifierDetails?.homeAddress?.stateId],

        mailingAddressSameAsPhysicalAddress: [this.notifierDetails?.mailingAddressSameAsPhysicalAddress ?? true],

        mailingAddressLine1: [this.notifierDetails?.mailingAddress?.line1],
        mailingAddressLine2: [this.notifierDetails?.mailingAddress?.line2],
        mailingAddressSuburb: [this.notifierDetails?.mailingAddress?.suburb],
        mailingAddressPostcode: [this.notifierDetails?.mailingAddress?.postcode],
        mailingAddressStateId: [this.notifierDetails?.mailingAddress?.stateId === undefined ? this.DEFAULT_STATE_QLD_ID : this.notifierDetails?.mailingAddress?.stateId],

      });
    }
    else {
      this.form = this.formBuilder.group({
        networkUserId: [this.notifierDetails?.username, [Validators.required]],
        canAttachEvidenceOfConsent: [this.notifierDetails?.canAttachEvidenceOfConsent, [Validators.required]],
        evidenceOfConsentProvided: [this.notifierDetails?.evidenceOfConsentProvided,],
        natureOfConsent: [this.notifierDetails?.natureOfConsent ?? ''],
        phoneOfficer: [this.notifierDetails?.phone, [VegValidators.phoneNumber]],
        location: [this.notifierDetails?.location, [Validators.required]],

        firstName: [this.notifierDetails?.firstName, [Validators.required, Validators.maxLength(20)]],
        middleName: [this.notifierDetails?.middleName],
        lastName: [this.notifierDetails?.lastName, [Validators.required]],

        email: [this.notifierDetails?.email, [VegValidators.email, Validators.required]],
      });
    }
  }

  ngOnInit(): void {

    this.evidenceOfConsentFileNames = this.notifierDetails?.evidenceOfConsentFileNames ?? [];
    this.evidenceOfConsentProvidedControl?.setValue(this.evidenceOfConsentFileNames)

    this.performValidations();


    this.referenceDataService.getStates$()
      .pipe(first(), untilDestroyed(this),
        tap(results => {
          this.states = results;
          this.DEFAULT_STATE_QLD_ID = results.filter(x => x.name == ReferencesEnum.queenslandState)[0].id ?? -1;
        }), mergeMap(x =>
          this.referenceDataService.getCountries$()
            .pipe(tap(result => {
              this.DEFAULT_COUNTRY_AUSTRALIA_ID = result.filter(x => x.name == ReferencesEnum.australiaCountry)[0].id ?? -1;
            }))
        )).subscribe();

    this.form.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.performValidations();
      });


    if (!this.isOfficer) {
      this.form.setValidators([this.atLeastOnePhoneNumberValidator()]);

      // Setting and clearing the validators of the mailing address fields so that the form doesn't get stuck when they're hidden
      // and clearing the mailing address values.
      this.mailingAddressSameAsPhysicalAddressControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(areAddressesEqual => {

          if (!areAddressesEqual) {
            this.mailingAddressLine1Control?.setValidators([Validators.required]);
            this.mailingAddressSuburbControl?.setValidators([Validators.required]);
            this.mailingAddressPostcodeControl?.setValidators([Validators.required, VegValidators.postcode]);
          }
          else {
            this.mailingAddressLine1Control.clearValidators();
            this.mailingAddressSuburbControl.clearValidators();
            this.mailingAddressPostcodeControl.clearValidators();

            const patch = {
              mailingAddressLine1: '',
              mailingAddressLine2: '',
              mailingAddressSuburb: '',
              mailingAddressPostcode: '',
              mailingAddressStateId: undefined,
              mailingAddressCountryId: undefined
            };
            this.form.patchValue(patch);
          }

          this.mailingAddressLine1Control?.updateValueAndValidity();
          this.mailingAddressPostcodeControl?.updateValueAndValidity();
          this.mailingAddressSuburbControl?.updateValueAndValidity();
        });
    }
  }

  performValidations() {

    this.canAttachEvidenceOfConsentControl?.setErrors(null);
    this.evidenceOfConsentProvidedControl?.setErrors(null);
    this.natureOfConsentControl?.setErrors(null);

    // if the lands officer  is true, perform action extra validation will be run, otherwise allows validation to be passed.
    if(!this.vwds.getLandholderConfirmationModel()?.areYouLandsOfficer) {
      return;
    }

    this.canAttachEvidenceOfConsentValid = !this.isOfficer || this.canAttachEvidenceOfConsentControl.value || !this.canAttachEvidenceOfConsentControl.value;
    this.evidenceOfConsentValid = this.nextButtonClicked
      ? (!this.isOfficer || !this.canAttachEvidenceOfConsentControl.value || this.evidenceOfConsentProvidedControl.value) && this.evidenceOfConsentFiles.length >= 1
      : !this.isOfficer || !this.canAttachEvidenceOfConsentControl.value || this.evidenceOfConsentProvidedControl.value;

    this.natureOfConsentPresentValid = this.nextButtonClicked
      ? !this.isOfficer || this.canAttachEvidenceOfConsentControl.value || (!!this.natureOfConsentControl.value && this.natureOfConsentControl.dirty)
      : !this.isOfficer || this.canAttachEvidenceOfConsentControl.value || (!this.natureOfConsentControl.value && this.natureOfConsentControl.pristine)
      || (!!this.natureOfConsentControl.value && this.natureOfConsentControl.dirty)

    if (!this.canAttachEvidenceOfConsentValid)
      this.canAttachEvidenceOfConsentControl?.setErrors({ 'Required': true });

    if (!this.evidenceOfConsentValid && this.canAttachEvidenceOfConsentControl.value)
      this.evidenceOfConsentProvidedControl?.setErrors({ 'Required': true });

    if (!this.natureOfConsentPresentValid && !this.canAttachEvidenceOfConsentControl.value)
      this.natureOfConsentControl?.setErrors({ 'Required': true });
  }

  atLeastOnePhoneNumberValidator(): ValidatorFn {
    return () => {
      const mobile = this.mobileControl;
      const phone = this.phoneControl;

      if (!this.mobileControl && !this.phoneControl) { return { atLeastOnePhoneRequiredValidationError: true } }

      if (!VegValidators.phoneNumber(this.mobileControl) || !VegValidators.phoneNumber(this.phoneControl)) { return null; }

      return mobile?.value?.length || phone?.value?.length ? null : { atLeastOnePhoneRequiredValidationError: true }
    };
  }

  private getNotifierDetails(): INotifierDetailsModel {
    const model = new NotifierDetailsModel();

    model.username = this.networkUserIdControl?.value;
    model.canAttachEvidenceOfConsent = this.canAttachEvidenceOfConsentControl?.value;
    model.location = this.locationControl?.value;

    if (this.isOfficer) {
      model.phone = this.phoneOfficerControl?.value;
    }
    else {
      model.phone = this.phoneControl?.value;
    }

    model.email = this.emailControl?.value;
    model.companyName = this.companyNameControl?.value;
    model.firstName = this.firstNameControl?.value;
    model.middleName = this.middleNameControl?.value;
    model.lastName = this.lastNameControl?.value;
    model.mobile = this.mobileControl?.value;
    model.evidenceOfConsentFileNames = this.evidenceOfConsentFileNames ?? [];
    model.natureOfConsent = this.natureOfConsentControl?.value;

    model.homeAddress = new AddressModel();
    model.homeAddress.line1 = this.homeAddressLine1Control?.value;
    model.homeAddress.line2 = this.homeAddressLine2Control?.value;
    model.homeAddress.countryId = this.DEFAULT_COUNTRY_AUSTRALIA_ID;
    model.homeAddress.postcode = this.homeAddressPostcodeControl?.value;
    model.homeAddress.stateId = this.DEFAULT_STATE_QLD_ID;
    model.homeAddress.suburb = this.homeAddressSuburbControl?.value;

    model.mailingAddressSameAsPhysicalAddress = this.mailingAddressSameAsPhysicalAddressControl?.value;

    if (model.mailingAddressSameAsPhysicalAddress) {
      model.mailingAddress = new AddressModel();
      model.mailingAddress.line1 = this.homeAddressLine1Control?.value;
      model.mailingAddress.line2 = this.homeAddressLine2Control?.value;
      model.mailingAddress.countryId = this.DEFAULT_COUNTRY_AUSTRALIA_ID;
      model.mailingAddress.postcode = this.homeAddressPostcodeControl?.value;
      model.mailingAddress.stateId = this.DEFAULT_STATE_QLD_ID;
      model.mailingAddress.suburb = this.homeAddressSuburbControl?.value;
    }
    else {
      model.mailingAddress = new AddressModel();
      model.mailingAddress.line1 = this.mailingAddressLine1Control?.value;
      model.mailingAddress.line2 = this.mailingAddressLine2Control?.value;
      model.mailingAddress.countryId = this.DEFAULT_COUNTRY_AUSTRALIA_ID;
      model.mailingAddress.postcode = this.mailingAddressPostcodeControl?.value;
      model.mailingAddress.stateId = this.DEFAULT_STATE_QLD_ID;
      model.mailingAddress.suburb = this.mailingAddressSuburbControl?.value;
    }
    return model;

  }

  removeConsent(evidenceOfConsent: string) {
    const fileNamesToKeep: string[] = [];

    if (this.evidenceOfConsentFiles && this.evidenceOfConsentFiles.length > 0) {
      const foundMatchingFile = this.evidenceOfConsentFiles.find(x => x.fileEntry.name === evidenceOfConsent);

      if (foundMatchingFile) {

        this.evidenceOfConsentFiles.splice(this.evidenceOfConsentFiles.indexOf(foundMatchingFile), 1);

        for (let index = 0; index < this.evidenceOfConsentFiles.length; index++) {
          fileNamesToKeep.push(this.evidenceOfConsentFiles[index].fileEntry.name);
        }

        this.evidenceOfConsentFileNames = fileNamesToKeep;
      }
    }
    else { // handle events where the page has been reloaded (when user click on previous screen)
      const foundMatchingFile = this.evidenceOfConsentFileNames.find(x => x === evidenceOfConsent);

      if (foundMatchingFile) {

        this.evidenceOfConsentFileNames.splice(this.evidenceOfConsentFileNames.indexOf(foundMatchingFile), 1);

        for (let index = 0; index < this.evidenceOfConsentFileNames.length; index++) {
          fileNamesToKeep.push(this.evidenceOfConsentFileNames[index]);
        }

        this.evidenceOfConsentFileNames = fileNamesToKeep;
      }
    }

    //post to server on what map attachments need to be kept
    this.fileManagementService.removeConsentFiles$(fileNamesToKeep)
      .pipe(first(), untilDestroyed(this),
        tap((response: any) => {
          if (!!response && response.length > 0) {
            this.evidenceOfConsentProvidedControl?.setValue(true);
            this.evidenceOfConsentError = '';
          }
          else {
            this.evidenceOfConsentProvidedControl?.setValue(false);
            this.evidenceOfConsentError = '';
          }
        }
        )).subscribe();
  }

  removeAllConsent() {
    this.fileManagementService.removeConsentFiles$([])
      .pipe(first(), untilDestroyed(this),
        tap(() => {

          this.evidenceOfConsentProvidedControl?.setValue(false);
          this.evidenceOfConsentError = '';

          this.evidenceOfConsentFileNames = [];
          this.evidenceOfConsentFiles = [];
        }))
      .subscribe();

  }

  uploadOrDroppedFiles(files: NgxFileDropEntry[]) {
    if (!files) {
      this.logger.warning('Some issues with the files content - please check');
      return;
    }

    const tenMegabytes = 10 * 1024 * 1024;
    let allFilesValid = true;
    const errorFilesCountMoreThanFive = [];
    const errorFilesOverTenMb: string[] = [];
    const errorFilesWithIncorrectMimeTypes: string[] = [];
    const errorFilesWithIllegalCharacters: string[] = [];
    const validFilesToUpload: NgxFileDropEntry[] = [];

    const concatFiles = this.evidenceOfConsentFiles.concat(files)
    const uniqueFilesBasedOnName = [...new Map(concatFiles.map((item) => [item.fileEntry.name, item])).values()];
    this.evidenceOfConsentFiles = uniqueFilesBasedOnName;

    if (this.evidenceOfConsentFiles.length > 5) {
      for (let index = this.evidenceOfConsentFiles.length - 1; index >= 5; index--) {
        const file = this.evidenceOfConsentFiles[index];
        this.evidenceOfConsentFiles.splice(index);
        files.splice(index);
        errorFilesCountMoreThanFive.push(file.fileEntry.name);
      }

      this.evidenceOfConsentError = 'Please select only a maximum of 5 files, alternatively you can zip the files. These files were skipped: '
        + errorFilesCountMoreThanFive.join(', ');
      allFilesValid = false;

    }

    for (let index = 0; index < this.evidenceOfConsentFiles.length; index++) {
      const droppedFile = this.evidenceOfConsentFiles[index];

      if (!droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        console.log(droppedFile.relativePath, fileEntry);
      }
      else {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          if (file.size > tenMegabytes) {
            this.evidenceOfConsentFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesOverTenMb.push(file.name);
            return;
          }

          if (!this.fileManagementService.validateMapMimeType(file) &&
            this.fileManagementService.validateFileEndingTypes(file)) {
            this.evidenceOfConsentFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesWithIncorrectMimeTypes.push(file.name);
            return;
          }

          if (!this.fileManagementService.fileHasValidCharacters(file)) {
            this.evidenceOfConsentFiles.splice(index);
            files.splice(index);
            allFilesValid = false;
            errorFilesWithIllegalCharacters.push(file.name);
            return;
          }

          validFilesToUpload.push(this.evidenceOfConsentFiles[index]);
        });
      }
    }


    if (!allFilesValid) {
      this.uploadValidNotifierFiles(validFilesToUpload);
      this.evidenceOfConsentError = '';

      if (errorFilesOverTenMb.length > 0) {
        this.evidenceOfConsentError += 'The file(s) you are trying to attach is too large please zip the file before continuing. It must be less than 10Mb each. See: '
          + errorFilesOverTenMb.join(', ');
      }

      if (errorFilesWithIncorrectMimeTypes.length > 0) {
        this.evidenceOfConsentError += 'The file is an unacceptable type. Please select an acceptable file - (.ZIP, .RAR), .DXF, .KML, .KMZ, TIFF, GeoTIFF, JPEG, GeoJPEG, Endas Imagine IMG format (BMP excluded), Arc Grid (.ZIP). See : '
          + errorFilesWithIncorrectMimeTypes.join(', ');
      }

      if (errorFilesCountMoreThanFive.length > 0) {
        this.evidenceOfConsentError = 'Please select only a maximum of 5 files, alternatively you can zip the files. These files were skipped: '
          + errorFilesCountMoreThanFive.join(', ');
      }

      if (errorFilesWithIllegalCharacters.length > 0) {
        this.evidenceOfConsentError = 'Please only upload files with names that consist of alphnumeric and _, @, ., #, &, +, -, or ~ '
          + errorFilesWithIllegalCharacters.join(', ');
      }
    }
    else {
      this.uploadValidNotifierFiles(this.evidenceOfConsentFiles);
      this.evidenceOfConsentError = '';
    }
  }

  uploadValidNotifierFiles(ngxFileDropEntries: NgxFileDropEntry[]) {

    this.fileManagementService.convertNgxFileDropEntriesToFileSystems$(ngxFileDropEntries)
      .pipe(untilDestroyed(this),
        concatMap((validFiles) => {
          if (validFiles.length > 0) {
            return this.fileManagementService.uploadEvidenceOfConsent$(validFiles)
              .pipe(tap(() => {
                const evidenceOfConsentFileNames: string[] = [];
                for (let index = 0; index < validFiles.length; index++) {
                  evidenceOfConsentFileNames.push(validFiles[index].name);
                }

                this.evidenceOfConsentFileNames = evidenceOfConsentFileNames;
                this.evidenceOfConsentProvidedControl?.setValue(true);
                console.log(this.evidenceOfConsentFileNames);
              }))
          }
          return of(null);
        }),
        catchError((err: any) => {
          this.logger.error('uploadValidNotifierFiles error', err);
          this.evidenceOfConsentError = 'The file is an unacceptable type. Please select an acceptable file - (.ZIP, .RAR), .DXF, .KML, .KMZ, TIFF, GeoTIFF, JPEG, GeoJPEG, Endas Imagine IMG format (BMP excluded), Arc Grid (.ZIP). Please try again!';
          return of();
        }))
      .subscribe();
  }

  public fileOver(event: any) {
    console.log('File over');
    console.log(event);
  }

  public fileLeave(event: any) {
    console.log('File leave');
    console.log(event);
  }

  override next(): void {
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
    this.nextButtonClicked = true;

    this.performValidations();

    // if the form isn't valid, don't allow next.
    if (!this.form.valid) {
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    this.notifierDetails = this.getNotifierDetails();

    this.vwds
      .setNotifierDetailsModel$(this.notifierDetails)
      .pipe(first(), untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          super.next();
        }
      });

  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }

  formatPostCode(event: KeyboardEvent) {
    const charCode = event.keyCode

    if ((charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }
}
